// Session handling for the public site.
//
// Added 2026-08-04 when PublicSiteController stopped being anonymous. The
// site now signs in with the SAME username and password the player uses in
// game — there is no separate site account. Sign-in posts the OAuth
// password grant to /connect/token, exactly as the admin portal does.
//
// The token lives in sessionStorage, not localStorage: it is cleared when
// the tab closes, which is the safer default for a shared or family
// machine. The trade-off is that players sign in again each session.

const KEY = 'tsunami.site.token';
const NAME_KEY = 'tsunami.site.user';

export function getToken(): string | null {
  try { return sessionStorage.getItem(KEY); } catch { return null; }
}

export function getUsername(): string | null {
  try { return sessionStorage.getItem(NAME_KEY); } catch { return null; }
}

export function clearSession() {
  try { sessionStorage.removeItem(KEY); sessionStorage.removeItem(NAME_KEY); } catch { /* ignore */ }
  notifyAuthChange();
}

// Listeners so <App> can re-render the moment a session starts or a 401
// invalidates one, without every page polling.
const listeners = new Set<() => void>();
export function onAuthChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function notifyAuthChange() {
  listeners.forEach(fn => fn());
}

/// Sign in with in-game credentials. Throws with a human-readable message
/// on failure so the login page can render it directly.
///
/// Posts to /api/site/v1/login, NOT /connect/token. The apex is a
/// static-host branch that clears the matched endpoint for any path outside
/// /api/, so a POST to /connect/token 404s there before reaching
/// AuthController. The admin UI hit the same wall and solved it the same
/// way with /api/admin/v1/login.
export async function signIn(username: string, password: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch('/api/site/v1/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
  } catch {
    throw new Error('Could not reach the server. Try again in a moment.');
  }

  if (!res.ok) {
    // Deliberately vague: do not confirm whether the username exists.
    throw new Error('Incorrect username or password.');
  }

  const body = await res.json().catch(() => null);
  const token = body?.access_token ?? body?.accessToken ?? body?.token;
  if (!token) throw new Error('Sign-in failed — the server returned no token.');

  try {
    sessionStorage.setItem(KEY, token);
    sessionStorage.setItem(NAME_KEY, username);
  } catch { /* private-mode browsers: session stays in memory only */ }
  notifyAuthChange();
}
